import { useState } from 'react';
import { useAuth } from '@/context/AuthContext';
import { MODES, getMode } from '@/constants/modes';
import { Loader2 } from 'lucide-react';

const ACCENTS = {
  lime: {
    active: 'bg-lime-400 text-black border-lime-400',
    hover: 'hover:border-lime-500 hover:text-lime-400',
    dot: 'bg-lime-400',
    label: 'text-lime-400',
    borderTop: 'border-t-lime-400',
  },
  amber: {
    active: 'bg-amber-400 text-black border-amber-400',
    hover: 'hover:border-amber-500 hover:text-amber-400',
    dot: 'bg-amber-400',
    label: 'text-amber-400',
    borderTop: 'border-t-amber-400',
  },
  sky: {
    active: 'bg-sky-400 text-black border-sky-400',
    hover: 'hover:border-sky-500 hover:text-sky-400',
    dot: 'bg-sky-400',
    label: 'text-sky-400',
    borderTop: 'border-t-sky-400',
  },
};

/**
 * Three-mode segmented control: Beginner / Pro / Student.
 * variant="compact"  — small pill for headers
 * variant="card"     — full card with tagline for Account page
 */
export default function ModeSwitcher({ variant = 'compact', className = '' }) {
  const { user, setMode } = useAuth();
  const currentId = user?.mode || 'beginner';
  const [busy, setBusy] = useState(null);

  const pick = async (id) => {
    if (id === currentId || busy) return;
    setBusy(id);
    try { await setMode(id); }
    catch (e) { console.warn('Mode update failed:', e); }
    finally { setBusy(null); }
  };

  if (variant === 'card') {
    const current = getMode(currentId);
    const accent = ACCENTS[current.color];
    return (
      <div data-testid="mode-card" className={`industrial-card border-t-4 ${accent.borderTop} ${className}`}>
        <div className="flex items-center gap-2 mb-3">
          <span className={`inline-block w-2 h-2 ${accent.dot}`} />
          <div className={`font-mono text-xs uppercase tracking-widest ${accent.label}`}>
            Learning Mode · {current.label}
          </div>
        </div>
        <h2 className="font-display text-3xl text-zinc-100 tracking-tight leading-none mb-1">
          PICK YOUR VIBE
        </h2>
        <p className="text-zinc-400 text-sm mb-4 leading-relaxed">
          Changes how your buddy talks and how much theory shows up. Switch any time.
        </p>
        <div className="grid grid-cols-3 gap-2" role="radiogroup" aria-label="Learning mode">
          {MODES.map((m) => {
            const a = ACCENTS[m.color];
            const active = m.id === currentId;
            return (
              <button
                key={m.id}
                type="button"
                role="radio"
                aria-checked={active}
                data-testid={`mode-btn-${m.id}`}
                onClick={() => pick(m.id)}
                disabled={busy === m.id}
                className={`relative p-3 border transition-colors font-mono uppercase text-xs tracking-widest ${
                  active ? a.active : `border-zinc-700 text-zinc-300 ${a.hover}`
                }`}
              >
                {busy === m.id ? <Loader2 size={12} className="animate-spin mx-auto" /> : m.label}
              </button>
            );
          })}
        </div>
        <div className="mt-3 text-xs font-mono text-zinc-500">
          {current.tagline}
        </div>
      </div>
    );
  }

  // compact variant
  return (
    <div
      data-testid="mode-switcher-compact"
      role="radiogroup"
      aria-label="Learning mode"
      className={`inline-flex border border-zinc-800 bg-zinc-950 ${className}`}
    >
      {MODES.map((m) => {
        const a = ACCENTS[m.color];
        const active = m.id === currentId;
        return (
          <button
            key={m.id}
            type="button"
            role="radio"
            aria-checked={active}
            data-testid={`mode-btn-${m.id}`}
            onClick={() => pick(m.id)}
            disabled={busy === m.id}
            title={m.tagline}
            className={`px-3 py-1.5 font-mono uppercase text-[11px] tracking-widest transition-colors ${
              active ? a.active : `text-zinc-400 ${a.hover}`
            }`}
          >
            {busy === m.id ? <Loader2 size={10} className="animate-spin" /> : m.label}
          </button>
        );
      })}
    </div>
  );
}
